import React, { Fragment, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

// Firebase
import { db } from '../config/firebase-config';
import { collection, getDocs, getDoc, addDoc, updateDoc, doc, setDoc, deleteDoc, query, where, onSnapshot, orderBy, startAfter, limit } from 'firebase/firestore';

import mixpanel from 'mixpanel-browser';

// Redux
import { connect } from 'react-redux';

// Actions
import { setPage, add_To_Recent_History, toggleAuthModal } from '../actions/navActions';

// Layout Wrapper
import Layout from '../components/layout/Layout';
import { getFollowingGroups, getGroupsBySearchTag, getMyGroups, getNewGroups, set_ShowBottomSpinner } from '../actions/groupActions';
import GroupBlock from '../components/Groups/GroupBlock';
import Spinner from '../components/common/Spinner';
import replaceHyphenWithSpace from '../utils/reusable_functions/replace_Hyphens_With_Space';

const DiscoverGroups = ({ 
    setPage, 
    add_To_Recent_History,
    match, 
    group: {
        groups, 
        no_more_groups,
        loading,
        show_bottom_spinner,
        last_page_doc
    },
    auth,
    nav: {
        website,
        website_loading
    },
    getGroupsBySearchTag,
    getFollowingGroups,
    getMyGroups,
    getNewGroups,
    set_ShowBottomSpinner
}) => {

    // State variable to track if Mixpanel event has been sent
    const [sentMixpanel, setSentMixpanel] = useState(false);

    // Window width
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);

    // Check if website is custom url or main website
    const [checked_WebsiteType, setChecked_WebsiteType] = useState(false);

    // -- Inifite scroll --

    // Temporarily disable to load more function to avoid duplicate fetch requests
    const [canLoadMore, setCanLoadMore] = useState(true);
    // Scroll direction - for displaying top nav bar
    const [scrollDirection, setScrollDirection] = useState("up");
    const [oldScrollY, setOldScrollY] = useState(window.scrollY);

    // -- END: Inifite scroll --

    const [groupsList, setGroupsList] = useState([]);

    // Get the current URL and extract the "redirect" parameter
    const url_filter = (window.location.href);
    const url = new URL(url_filter);
    const hostname = window.location.hostname;
    const search_tag = match.params.search_tag?.toLowerCase();

    // Determine window size breakpoints
    const isMobile = windowWidth <= 769;
    const isTablet = windowWidth <= 1000;

    // Listen for window resize event and update window width state
    useEffect(() => {

        // Set the current page to 'likes'
        setPage('discover groups');
        add_To_Recent_History(url.pathname);  // current url

        // Add event listener for window resize
        window.addEventListener('resize', () => handleWindowSizeChange());

        // Clean up by removing event listener on component unmount
        return () => window.removeEventListener('resize', () => handleWindowSizeChange());
    }, []);

    // Update window width state on window resize
    const handleWindowSizeChange = () => {
        setWindowWidth(window.innerWidth);
    };

    // -- Get initial groups list --

    useEffect(() => {
        if(search_tag === 'following') {
            return; // do nothing
        } else if (search_tag === 'my-groups') {
            return; // do nothing
        } else if (search_tag === 'new-groups') {
            getNewGroups();
        } else {
            getGroupsBySearchTag(search_tag);
        }

    }, [search_tag]);

    useEffect(() => {
        if(auth?.user) {
            if(search_tag === 'following') {
                getFollowingGroups(auth.user);
            } else if (search_tag === 'my-groups') {
                getMyGroups(auth.user);
            }
        }

    }, [ search_tag, auth.user ]);

    // -- END: Get initial groups list --

    useEffect(() => {

        renderGroupList();

    }, [ groups, loading, canLoadMore ]);

    // Handle top nav bar display
    const handleScroll = (e) => {
        const { offsetHeight, scrollTop, scrollHeight } = e.target

        const newScrollY = offsetHeight + scrollTop;

        // --- Handle top nav bar display
        if(isMobile) {
            if(oldScrollY < newScrollY){

                // scrolling down - hide top nav
                setScrollDirection("down");
    
                if(document.getElementById('feed-header') !== null) {
                    document.getElementById('feed-header').classList.remove("active");
                }
                
            } else {
    
                // scrolling up - show top nav
                setScrollDirection("up");
    
                if(document.getElementById('feed-header') !== null) {
                    document.getElementById('feed-header').classList.add("active");
                }
            }
        }
        // --- END Handle top nav bar display

        // update the scroll position for future comparison
        setOldScrollY(newScrollY)

        // Check if the user has scrolled to the bottom of the content and conditions for loading more notifications are met
        if (newScrollY >= scrollHeight - 1 && !show_bottom_spinner && !no_more_groups && groups.length > 0 && canLoadMore) {
            console.log('scrolling');
            loadMore();
        }
    }

    // Function to load more paginated notifications
    const loadMore = async () => {
        console.log('Loading more groups to discover');

        try {
            // Temporarily disable to load more function to avoid duplicate fetch requests
            setCanLoadMore(false);
            // Show spinner
            set_ShowBottomSpinner(true);

            if(search_tag === 'following') {
                // await getFollowingGroups(auth.user);
                return; // can't load more following groups b/c users can only follow 30 groups MAX
            } else if (search_tag === 'my-groups') {
                await getMyGroups(auth.user, last_page_doc);
            } else if (search_tag === 'new-groups') {
                await getNewGroups(last_page_doc);
            } else {
                await getGroupsBySearchTag(search_tag, last_page_doc);
            }
        } catch (error) {
            console.log("ERROR loading more", error);
        } finally {
            // Re-enable the load more capabilty
            setCanLoadMore(true);
        }

    }

    // Handles Mixpanel tracking
    const handleMixpanel = () => {
        mixpanel.init(process.env.REACT_APP_MIXPANEL_ID);

        // Track event
        if(search_tag === 'following') {
            mixpanel.track("Following Groups Page View");
        } else if (search_tag === 'my-groups') {
            mixpanel.track("My Groups Page View");
        } else if (search_tag === 'new-groups') {
            mixpanel.track("New Groups Page View");
        } else {
            mixpanel.track("Discover Groups Page View");
        }
    }

    // Checks if the environment is production and Mixpanel tracking hasn't been sent
    if(process.env.NODE_ENV === 'production' && !sentMixpanel && !hostname.includes('xbuysell')) {
        // Call Mixpanel tracking function
        handleMixpanel();
        // Set Mixpanel event as sent
        setSentMixpanel(true);
    }

    const renderGroupList = async () => {
        setGroupsList([]);

        if(loading) {
            setGroupsList([(
                <div className="no-rides">
                    <Spinner />
                </div>
            )])
        }
        else { 
            if(groups.length > 0) {
                groups.map(group_doc => {
                    setGroupsList(groupsList => [...groupsList, (
                        <GroupBlock group_doc={group_doc} />
                    )])
                }) 
            } else {
                setGroupsList(userList => [...userList, (
                    <div className="no-rides">
                        <h1>No groups found</h1>
                    </div>
                )])
                
            }
        } 
    }

    // Check if website is custom url or main website (this page is only for custom URLs)
    if(website && !checked_WebsiteType) {
        window.location.href = '/home';

        setChecked_WebsiteType(true);
    }

    return (
        <Fragment>
            <Layout handleScroll={handleScroll} page="discover groups" page_title={replaceHyphenWithSpace(search_tag)}>
                
                {/* spacer */}
                <div style={{height: '5px'}}></div>
                {groupsList}

                {/* Render a spinner at the bottom if there are more notifications to load */}
                {show_bottom_spinner && !no_more_groups && !loading && <Spinner />}

                {/* Display a message when there are no more notifications */}
                <div className="no-rides" style={{minHeight: '400px', justifyContent: 'start', paddingTop: '50px'}}>{(no_more_groups && groups.length !== 0) && "You've reached the end."}</div>
            </Layout>
        </Fragment>
    )
}

DiscoverGroups.propTypes = {
    auth: PropTypes.object.isRequired,
    nav: PropTypes.object.isRequired,
    group: PropTypes.object.isRequired, // Object prop representing the group state
    setPage: PropTypes.func.isRequired, // Function prop to set the current page in the navigation
    add_To_Recent_History: PropTypes.func.isRequired,
    getGroupsBySearchTag: PropTypes.func.isRequired,
    getNewGroups: PropTypes.func.isRequired,
    getFollowingGroups: PropTypes.func.isRequired,
    getMyGroups: PropTypes.func.isRequired,
    set_ShowBottomSpinner: PropTypes.func.isRequired,
}

const mapStateToProps = state => ({

    // Mapping the states from the Redux store to the below props
    group: state.group,
    auth: state.auth,
    nav: state.nav
});

export default connect(mapStateToProps, { 

    // Connecting actions to the component
    setPage, 
    add_To_Recent_History,
    getGroupsBySearchTag,
    getNewGroups,
    getMyGroups,
    getFollowingGroups,
    set_ShowBottomSpinner
})(DiscoverGroups);
